import { api } from '../client';
import { unwrap } from '../unwrap';
import type { User } from '../types';

export type ReasoningEffort = 'auto' | 'none' | 'low' | 'medium' | 'high';

export type IdentityType = 'email' | 'sms' | 'apple' | 'wechat';

export interface AuthIdentity {
  identity_type: IdentityType;
  identifier: string;
  created_at: string;
}

export interface UserSettings {
  reasoning_effort: ReasoningEffort | null;
  timezone: string | null;
  asr_hotwords_enabled: boolean;
  asr_hotwords: string[];
  updated_at: string | null;
}

export interface UserUpdatePayload {
  nickname?: string | null;
  avatar_url?: string | null;
}

export interface UserSettingsUpdatePayload {
  reasoning_effort?: ReasoningEffort | null;
  timezone?: string | null;
  asr_hotwords_enabled?: boolean;
  asr_hotwords?: string[];
}

export const usersApi = {
  async me(): Promise<User> {
    const raw = await api.get<unknown>('/api/v1/users/me');
    return unwrap<User>(raw);
  },

  async update(payload: UserUpdatePayload): Promise<User> {
    const raw = await api.put<unknown>('/api/v1/users/me', payload);
    return unwrap<User>(raw);
  },

  async identities(): Promise<AuthIdentity[]> {
    const raw = await api.get<unknown>('/api/v1/users/me/identities');
    const data = unwrap<{ identities: AuthIdentity[] }>(raw);
    return data.identities ?? [];
  },

  async settings(): Promise<UserSettings> {
    const raw = await api.get<unknown>('/api/v1/users/me/settings');
    return unwrap<UserSettings>(raw);
  },

  async updateSettings(payload: UserSettingsUpdatePayload): Promise<UserSettings> {
    const raw = await api.put<unknown>('/api/v1/users/me/settings', payload);
    return unwrap<UserSettings>(raw);
  },

  async deleteAccount(): Promise<void> {
    await api.delete<unknown>('/api/v1/users/me');
  },
};
